// src/pages/ProductionDashboard.js
import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import styled from 'styled-components';
import KpiCard from 'components/charts/KpiCard';
import WeightRunChart from 'components/charts/WeightRunChart';
import TimeSeriesChart from 'components/charts/TimeSeriesChart';
import PalletsTable from 'components/charts/PalletsTable';
import { getTraceabilityProductionReport } from 'services/traceabilityProductionReport/service';
import { finishRequest, startRequest } from 'store/modules/loading/actions';
import { sanitizeErrorUI } from 'utils/errorUtil';
import { setMessage } from 'store/modules/message/action';
import { useDispatch } from 'react-redux';

const DashboardWrapper = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 24px;
`;

const DashboardCard = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px 24px 32px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SmallSection = styled.div`
  margin-top: 16px;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const KpiSectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
`;

const OrderInfoCard = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid #eeeeee;
`;

const OrderInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 8px 24px;
`;

const OrderInfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrderInfoLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 2px;
`;

const OrderInfoValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #212121;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

function utcToBrazilTime(timestamp) {
  if (!timestamp) return timestamp;

  let utcDate;

  // já é Date
  if (timestamp instanceof Date) {
    utcDate = timestamp;
  }

  // ISO UTC
  else if (
    typeof timestamp === 'string' &&
    timestamp.includes('T')
  ) {
    utcDate = new Date(timestamp);
  }

  // formato SQL: YYYY-MM-DD HH:mm
  else if (typeof timestamp === 'string') {
    utcDate = new Date(
      timestamp.replace(' ', 'T') + ':00Z'
    );
  }

  else {
    return timestamp;
  }

  if (isNaN(utcDate.getTime())) {
    return timestamp;
  }

  return utcDate.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
}

function convertDatasetTimezone(
  data,
  field = 'timestamp'
) {
  if (!Array.isArray(data)) return data;

  return data.map((item) => {
    if (!item[field]) {
      return item;
    }

    return {
      ...item,
      [field]: utcToBrazilTime(item[field]),
    };
  });
}

const ProductionDashboard = ({ match }) => {
  const lot = match.params.lot;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!lot) return;

    async function fetchData() {
      try {
        dispatch(startRequest());
        getTraceabilityProductionReport(lot).then(data => {
          const responseData = data.data;

          // ============================
          // CONVERSÃO TIMEZONE
          // ============================

          responseData.unitsPerInterval =
            convertDatasetTimezone(
              responseData.unitsPerInterval
            );

          responseData.kgPerInterval =
            convertDatasetTimezone(
              responseData.kgPerInterval
            );

          responseData.cumulative =
            convertDatasetTimezone(
              responseData.cumulative
            );

          responseData.weightRunChart =
            convertDatasetTimezone(
              responseData.weightRunChart
            );

          // se pallets tiver timestamp
          if (responseData.pallets) {
            responseData.pallets =
              responseData.pallets.map((item) => ({
                ...item,

                closedAt: item.closedAt
                  ? utcToBrazilTime(item.closedAt)
                  : item.closedAt,

                createdAt: item.createdAt
                  ? utcToBrazilTime(item.createdAt)
                  : item.createdAt,
              }));
          }

          setSummary(responseData);
        }).catch(err => {
          dispatch(setMessage(sanitizeErrorUI(err)));
        }).finally(() => {
          dispatch(finishRequest());
        });

      } catch (err) {
        console.error('Erro ao carregar dashboard', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [dispatch, lot]);

  const isLoadingOrEmpty = loading || !summary;

  return (
    <DashboardWrapper>
      <DashboardCard>

        {/* FILTROS */}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5">Dashboard de Produção</Typography>
          </Grid>
        </Grid>

        {/* BLOCO NOVO: INFORMAÇÕES DA ORDEM */}
        {!isLoadingOrEmpty && (
          <OrderInfoCard>
            <OrderInfoGrid>
              <OrderInfoItem>
                <OrderInfoLabel>Número do lote</OrderInfoLabel>
                <OrderInfoValue>{summary.productionLot}</OrderInfoValue>
              </OrderInfoItem>

              <OrderInfoItem>
                <OrderInfoLabel>Código do produto</OrderInfoLabel>
                <OrderInfoValue>
                  {summary.productCode}
                  {summary.productDescription
                    ? ` - ${summary.productDescription}`
                    : ''}
                </OrderInfoValue>
              </OrderInfoItem>

              <OrderInfoItem>
                <OrderInfoLabel>Código da unidade</OrderInfoLabel>
                <OrderInfoValue>{summary.productUnity}</OrderInfoValue>
              </OrderInfoItem>

              <OrderInfoItem>
                <OrderInfoLabel>Tipo de embalagem</OrderInfoLabel>
                <OrderInfoValue>{summary.packageType}</OrderInfoValue>
              </OrderInfoItem>
            </OrderInfoGrid>
          </OrderInfoCard>
        )}

        {isLoadingOrEmpty ? (
          <Section>
            <Typography>
              {loading ? 'Carregando dados...' : 'Selecione uma ordem para visualizar.'}
            </Typography>
          </Section>
        ) : (
          <>
            {/* ====================== KPIs PRODUÇÃO ====================== */}
            <Section>
              <KpiSectionTitle>Produção</KpiSectionTitle>
              <KpiGrid>
                <KpiCard label="Planejado" value={summary.plannedQty} unit="un" />
                <KpiCard label="Produzido" value={summary.producedQty} unit="un" />
                <KpiCard label="% Concluído" value={summary.completionPercent} unit="%" />
                <KpiCard label="Velocidade atual" value={summary.currentRate} unit="un/h" />
                <KpiCard label="Velocidade média" value={summary.avgRate} unit="un/h" />
                <KpiCard label="ETA conclusão" value={summary.etaMinutes} unit="min" />
              </KpiGrid>
            </Section>

            {/* ====================== KPIs PESO ====================== */}
            <SmallSection>
              <KpiSectionTitle>Qualidade / Peso</KpiSectionTitle>
              <KpiGrid>
                <KpiCard label="Peso alvo" value={summary.targetWeight} unit="kg" />
                <KpiCard label="Peso médio" value={summary.avgWeight} unit="kg" />
                <KpiCard label="Desvio padrão" value={summary.stdWeight} unit="kg" />
                <KpiCard label="% fora da faixa" value={summary.outOfSpecPercent} unit="%" />
                <KpiCard label="Paletes fechados" value={summary.closedPallets} />
                <KpiCard label="Peso total" value={summary.totalWeight} unit="kg" />
              </KpiGrid>
            </SmallSection>

            {/* ====================== GRÁFICOS ====================== */}
            <Section>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TimeSeriesChart
                    title={`Unidades (${summary.interval}) minutos`}
                    data={summary.unitsPerInterval}
                    xKey="timestamp"
                    series={[{ key: 'units', label: 'Unidades', stroke: '#1976d2' }]}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <WeightRunChart
                    title="Peso x tempo"
                    data={summary.weightRunChart}
                    targetWeight={summary.targetWeight}
                    lowerLimit={summary.weightLowerLimit}
                    upperLimit={summary.weightUpperLimit}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TimeSeriesChart
                    title={`Kg (${summary.interval}) minutos`}
                    data={summary.kgPerInterval}
                    xKey="timestamp"
                    series={[{ key: 'kg', label: 'Kg', stroke: '#388e3c' }]}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TimeSeriesChart
                    title="Produção acumulada vs ideal"
                    data={summary.cumulative}
                    xKey="timestamp"
                    series={[
                      { key: 'ideal', label: 'Ideal', stroke: '#d32f2f' },
                      { key: 'real', label: 'Real', stroke: '#1976d2' },
                    ]}
                  />
                </Grid>
              </Grid>
            </Section>

            {/* ====================== TABELA DE PALETES ====================== */}
            <Section>
              <KpiSectionTitle>Paletes</KpiSectionTitle>
              <PalletsTable data={summary.pallets} />
            </Section>

          </>
        )}
      </DashboardCard>
    </DashboardWrapper>
  );
};

export default ProductionDashboard;
